import React, { useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { NewPadVM } from './NewPadVM';
import { FlatButton } from 'nrcs-design-system';
import { mdiTargetVariant, mdiCursorMove } from '@mdi/js';
import { Notification } from 'cd-eft/components/common/Notification';
import padStructureDiagram from '../../../assets/images/pad_layout.jpg';
import useImageLoaded from 'cd-eft/utils/useImageLoaded';
import stores from '../../../models/Stores';
import './newPad.scss';
import { MapView, Sketch } from 'cd-eft/utils/esriTypes';
import { PitDesignStore } from 'cd-eft/models/Stores/PitDesignStore';
import { toDecimalPrecision } from 'cd-eft/utils/mathUtilities';
import { UserLeavingWarning } from 'cd-eft/components/common/UserLeavingWarning/UserLeavingWarning';

const { padDesignStore, designStore } = stores.AppStore;
interface INewPadProps {
  mapView: MapView | undefined;
  drawRectangularBase: (designForm: any) => void;
  updateBaseGraphic: (designForm: any) => void;
  removeBaseStructureGraphics: () => void;
  removeCenterMarker: () => void;
  chooseCenterPoint();
  redefineSketch();
}

const NewPadComponent = (props: INewPadProps) => {
  const history = useHistory();
  const vmRef = useRef<NewPadVM>();
  if (!vmRef.current) {
    vmRef.current = new NewPadVM(props);
  }
  const vm = vmRef.current;
  const [ref, loaded, onLoad] = useImageLoaded();

  useEffect(() => {
    vm.getErrors();
    vm.onMount(history);
    function unmount() {
      vm.onUnmount();
    }
    return unmount;
  }, []);

  return (
    <div className="structure-new-pad-form">
      {/* popups  */}
      {(stores.AppStore.designStore.hasProject()) && (padDesignStore.pendingEdits.design || padDesignStore.pendingEdits.unsavedEdits || padDesignStore.pendingEdits.name) && (
        <UserLeavingWarning
          when={stores.AppStore.designStore.hasProject() && (padDesignStore.pendingEdits.design || padDesignStore.pendingEdits.unsavedEdits || padDesignStore.pendingEdits.name)}
          section="Pad Design"
          onOK={vm.userLeavingWarningOnOk}
          okText="Return"
          text={
            vm.notification.openPad
          }
        />
      )}

      <fieldset disabled={!designStore.hasProject() || !designStore.hasSurfaceTest()}>
        <div className="row pad-name">
          <input
            aria-required="true"
            aria-label="Pad Name"
            id="name"
            name="name"
            value={vm.newPadForm.name}
            onChange={(e) => vm.handleInputChange(e)}
            className={'mr-2' + vm.errorClass('name')}
            placeholder="Pad name..."
          />
        </div>
        <span className="usa-error-message" id="new-pad-name-error--message" role="alert">
          <div>{vm.formValidation.formErrors['name']}</div>
        </span>

        <div className="structure-diagram-container">
          <img
            ref={ref}
            onLoad={onLoad}
            role="presentation"
            id="pad-structure-diagram"
            src={padStructureDiagram}
            alt="rectangular structure diagram"
          />
          {loaded ? (
            <>
              {/* Length & Width */}
              <label className="usa-label" htmlFor="width" id="pad-width-label">
                PW
              </label>
              <input
                className={'usa-input structure-input' + vm.errorClass('width')}
                id="pad-width"
                aria-required="true"
                name="width"
                type="number"
                value={vm.newPadForm.width}
                onChange={vm.handleInputChange}
                onBlur={vm.handleOnBlurRounding}
              />
              <label className="usa-label" htmlFor="length" id="pad-length-label">
                PL
              </label>
              <input
                className={'usa-input structure-input' + vm.errorClass('length')}
                id="pad-length"
                aria-required="true"
                name="length"
                type="number"
                value={vm.newPadForm.length}
                onChange={vm.handleInputChange}
                onBlur={vm.handleOnBlurRounding}
              />
              {/* Cut Slopes */}
              <label className="usa-label" htmlFor="cut-slope-1" id="cut-slope-1-label">
                CS1
              </label>
              <input
                className={
                  'usa-input structure-input' +
                  (vm.formValidation['cut-slope-1Valid'] ? '' : vm.errorClass('cut-slope-1'))
                }
                id="cut-slope-1"
                aria-required="true"
                name="cut-slope-1"
                type="number"
                value={vm.newPadForm.cutSlopes[0]}
                onChange={vm.handleInputChange}
                onBlur={vm.handleOnBlurRounding}
              />
              <label className="usa-label" htmlFor="cut-slope-2" id="cut-slope-2-label">
                CS2
              </label>
              <input
                className={
                  'usa-input structure-input' +
                  (vm.formValidation['cut-slope-2Valid'] ? '' : vm.errorClass('cut-slope-2'))
                }
                id="cut-slope-2"
                aria-required="true"
                name="cut-slope-2"
                type="number"
                value={vm.newPadForm.cutSlopes[1]}
                onChange={vm.handleInputChange}
                onBlur={vm.handleOnBlurRounding}
              />
              <label className="usa-label" htmlFor="cut-slope-3" id="cut-slope-3-label">
                CS3
              </label>
              <input
                className={
                  'usa-input structure-input' +
                  (vm.formValidation['cut-slope-3Valid'] ? '' : vm.errorClass('cut-slope-3'))
                }
                id="cut-slope-3"
                aria-required="true"
                name="cut-slope-3"
                type="number"
                value={vm.newPadForm.cutSlopes[2]}
                onChange={vm.handleInputChange}
                onBlur={vm.handleOnBlurRounding}
              />
              <label className="usa-label" htmlFor="cut-slope-4" id="cut-slope-4-label">
                CS4
              </label>
              <input
                className={
                  'usa-input structure-input' +
                  (vm.formValidation['cut-slope-4Valid'] ? '' : vm.errorClass('cut-slope-4'))
                }
                id="cut-slope-4"
                aria-required="true"
                name="cut-slope-4"
                type="number"
                value={vm.newPadForm.cutSlopes[3]}
                onChange={vm.handleInputChange}
                onBlur={vm.handleOnBlurRounding}
              />
              {/* Fill Slopes */}
              <label className="usa-label" htmlFor="fill-slope-1" id="fill-slope-1-label">
                FS1
              </label>
              <input
                className={
                  'usa-input structure-input' +
                  (vm.formValidation['fill-slope-1Valid'] ? '' : vm.errorClass('fill-slope-1'))
                }
                id="fill-slope-1"
                aria-required="true"
                name="fill-slope-1"
                type="number"
                value={vm.newPadForm.fillSlopes[0]}
                onChange={vm.handleInputChange}
                onBlur={vm.handleOnBlurRounding}
              />
              <label className="usa-label" htmlFor="fill-slope-2" id="fill-slope-2-label">
                FS2
              </label>
              <input
                className={
                  'usa-input structure-input' +
                  (vm.formValidation['fill-slope-2Valid'] ? '' : vm.errorClass('fill-slope-2'))
                }
                id="fill-slope-2"
                aria-required="true"
                name="fill-slope-2"
                type="number"
                value={vm.newPadForm.fillSlopes[1]}
                onChange={vm.handleInputChange}
                onBlur={vm.handleOnBlurRounding}
              />
              <label className="usa-label" htmlFor="fill-slope-3" id="fill-slope-3-label">
                FS3
              </label>
              <input
                // className={'usa-input structure-input' + vm.errorClass('fill-slope-3')}
                className={
                  'usa-input structure-input' +
                  (vm.formValidation['fill-slope-3Valid'] ? '' : vm.errorClass('fill-slope-3'))
                }
                id="fill-slope-3"
                aria-required="true"
                name="fill-slope-3"
                type="number"
                value={vm.newPadForm.fillSlopes[2]}
                onChange={vm.handleInputChange}
                onBlur={vm.handleOnBlurRounding}
              />
              <label className="usa-label" htmlFor="fill-slope-4" id="fill-slope-4-label">
                FS4
              </label>
              <input
                className={
                  'usa-input structure-input' +
                  (vm.formValidation['fill-slope-4Valid'] ? '' : vm.errorClass('fill-slope-4'))
                }
                id="fill-slope-4"
                aria-required="true"
                name="fill-slope-4"
                type="number"
                value={vm.newPadForm.fillSlopes[3]}
                onChange={vm.handleInputChange}
                onBlur={vm.handleOnBlurRounding}
              />
            </>
          ) : null}
        </div>

        {/* Errors, Equal Cut/Fill SLopes and Pick Up Center Button */}
        <span className="usa-error-message" id="new-pad-error--message" role="alert">
          <div>{vm.formValidation.formErrors['width']}</div>
          <div>{vm.formValidation.formErrors['length']}</div>
          <div>{vm.slopeErrorMsg}</div>
          <div>{vm.errorMsg}</div>
        </span>
        <div className="mr-1 ml-1">
          <div className="row">
            <div className="col-sm-6">
              <input
                className="usa-checkbox__input"
                data-behavior="select-table-row"
                id="equalFillSlopes"
                type="checkbox"
                name="equalFillSlopes"
                value="equalFillSlopes"
                checked={vm.newPadForm.equalFillSlopes}
                onChange={vm.toggleEqualFillSlopes}
              />
              <label className="usa-checkbox__label align-items-center" htmlFor="equalFillSlopes">
                Use Equal Fill Slopes
              </label>
            </div>
            <div className="col-sm-6 pick-up-center-button">
              <div className="pick-center-btn">
                <FlatButton
                  title={'select center from map'}
                  iconLeft={mdiTargetVariant}
                  onClick={() => {
                    if (padDesignStore.chooseCenter) {
                      padDesignStore.chooseCenter = false;
                    }
                    else {
                      padDesignStore.chooseCenter = true;
                    }
                    vm.checkStructurePendingEdits('center');
                    props.chooseCenterPoint();
                    vm.clearCenterErrorMessages();
                  }}
                  className={` ${padDesignStore.chooseCenter ? 'active' : ''}`}
                  disabled={stores.AppStore.designStore.sceneViewActive}
                >
                  <span>Select Center</span>
                </FlatButton>
                <div className="choose-center-warn-msg">only available on 2D view</div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <input
                className="usa-checkbox__input"
                data-behavior="select-table-row"
                id="equalCutSlopes"
                type="checkbox"
                name="equalCutSlopes"
                value="equalCutSlopes"
                checked={vm.newPadForm.equalCutSlopes}
                onChange={vm.toggleEqualCutSlopes}
              />
              <label className="usa-checkbox__label align-items-center" htmlFor="equalCutSlopes">
                Use Equal Cut Slopes
              </label>
            </div>
            <div className="col">
              <div className="pick-up-center-button">
                <FlatButton
                  title="move and rotate the structure on the map"
                  iconLeft={mdiCursorMove}
                  aria-label={`${vm.sketchActive ? 'active' : ''}`}
                  className={`move-rotate-button ${vm.sketchActive ? 'active' : ''}`}
                  onClick={() => {
                    vm.activateMoveRotateSketch();
                    vm.checkStructurePendingEdits('center');
                  }}
                >
                  <span>Move/Rotate</span>
                </FlatButton>
              </div>
            </div>
          </div>
        </div>

        {/* Center X, Y,  Top Z, Rotation Inputs */}
        <div className="row">
          <div className="col-md-6">
            <label className="usa-label" htmlFor="x-center">
              Center X
            </label>
            <input
              className={'usa-input' + vm.errorClass('x')}
              id="x-center"
              aria-required="true"
              data-testid="pad-center-x"
              name="x"
              type="number"
              value={toDecimalPrecision(vm.newPadForm.center.x, 3)}
              onChange={vm.handleInputChange}
              onBlur={vm.handleOnBlurRounding}
            />
            <span className="usa-error-message" id="new-pad-error--message-x-y-z" role="alert">
              {vm.formValidation.formErrors['x'] && (
                <div>Center X {vm.formValidation.formErrors['x']}</div>
              )}
            </span>
            <label className="usa-label" htmlFor="z-center">
              Top Z
            </label>
            <input
              className={'usa-input' + vm.errorClass('z')}
              id="z-center"
              aria-required="true"
              data-testid="pad-center-z"
              name="z"
              type="number"
              value={toDecimalPrecision(vm.newPadForm.center.z, 3)}
              onChange={vm.handleInputChange}
              onBlur={vm.handleOnBlurRounding}
            />
            <span className="usa-error-message" id="new-pad-error--message-x-y-z" role="alert">
              {vm.formValidation.formErrors['z'] && (
                <div>Top Z {vm.formValidation.formErrors['z']}</div>
              )}
            </span>
          </div>

          <div className="col-md-6">
            <label className="usa-label" htmlFor="y-center">
              Center Y
            </label>
            <input
              className={'usa-input' + vm.errorClass('y')}
              id="y-center"
              aria-required="true"
              data-testid="pad-center-y"
              name="y"
              type="number"
              value={toDecimalPrecision(vm.newPadForm.center.y, 3)}
              onChange={vm.handleInputChange}
              onBlur={vm.handleOnBlurRounding}
            />
            <span className="usa-error-message" id="new-pad-error--message-x-y-z" role="alert">
              {vm.formValidation.formErrors['y'] && (
                <div>Center Y {vm.formValidation.formErrors['y']}</div>
              )}
            </span>
            <div>
              <label className="usa-label" htmlFor="orienAzimuth">
                Rotation Azimuth
              </label>
              <input
                className={'usa-input' + vm.errorClass('orienAzimuth')}
                id="orienAzimuth"
                aria-required="true"
                name="orienAzimuth"
                type="number"
                value={toDecimalPrecision(vm.newPadForm.orienAzimuth, 2)}
                onChange={vm.handleInputChange}
                onBlur={vm.handleOnBlurRounding}
              />
              <span className="usa-error-message" id="new-pad-error--message-x-y-z" role="alert">
                <div>{vm.formValidation.formErrors['orienAzimuth']}</div>
              </span>
            </div>
          </div>
        </div>

        <div className="row">
          <button className="usa-button design-button" onClick={vm.generateStructure}
            disabled={
              vm.isLoading ||
              (!padDesignStore.newDesign && padDesignStore.pendingEdits.name && !padDesignStore.pendingEdits.design) ||
              (padDesignStore.newDesign && !padDesignStore.pendingEdits.design)
            }
          >
            Design
          </button>
        </div>

        <div className="row">
          <div className="calculations-container">
            <h4>Structure Calculations</h4>
            <div className="child child-one">
              Cut Volume (cy):{' '}
              {padDesignStore.newPadResults?.cutVolume
                ? toDecimalPrecision(padDesignStore.newPadResults.cutVolume, 2)
                : '--'}
            </div>
            <div className="child">
              Cut/Fill Balance (cy):{' '}
              {padDesignStore.newPadResults?.cutFillBalance
                ? toDecimalPrecision(padDesignStore.newPadResults.cutFillBalance, 2)
                : '--'}
            </div>
            <div className="child child-one">
              Fill Volume (cy):{' '}
              {padDesignStore.newPadResults?.fillVolume
                ? toDecimalPrecision(padDesignStore.newPadResults.fillVolume, 2)
                : '--'}
            </div>
            <div className="child">
              Cut/fill ratio:{' '}
              {padDesignStore.newPadResults?.cutFillRatio
                ? toDecimalPrecision(padDesignStore.newPadResults.cutFillRatio, 2)
                : '--'}
            </div>
          </div>
        </div>
      </fieldset>
      <div className="row justify-content-end">
        <div>
          <button
            hidden={!padDesignStore.editDesign}
            className="usa-button float-right"
            onClick={vm.updateDesignElement}
            disabled={
              //disable the form when form has no change or the numberic form fields are updated after design
              //disable the save button when name field is empty
              !padDesignStore.newDesignForm.name ||
              (!padDesignStore.newDesign && !padDesignStore.pendingEdits.name && !padDesignStore.pendingEdits.design) || padDesignStore.pendingEdits.design
            }
          >
            Accept Edits
          </button>
          <button
            hidden={padDesignStore.editDesign}
            className="usa-button float-right"
            onClick={vm.postAddDesignElement}
            disabled={
              !padDesignStore.newDesignForm.name ||
              !padDesignStore.newPadResults?.borders ||
              (!padDesignStore.newDesign && !padDesignStore.pendingEdits.name && !padDesignStore.pendingEdits.design) ||
              padDesignStore.pendingEdits.design
            }
          >
            Save Structure
          </button>
          <button
            className="usa-button usa-button--outline float-right"
            onClick={() => {
              props.redefineSketch();
              vm.cancelAddDesignElement(true)
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const NewPad = observer(NewPadComponent);
